import { useEffect, useMemo, useRef, useState } from "react";
import {
  IconChevronLeft,
  IconChevronRight,
  IconDeviceFloppy,
  IconDownload,
  IconEye,
  IconPlus,
  IconSearch,
  IconTicket,
  IconTrash,
} from "@tabler/icons-react";
import { useFormik } from "formik";
import * as yup from "yup";
import CmsLayout from "@layouts/CmsLayout";
import AppModal from "@components/modals/AppModal";
import ConfirmDeleteModal from "@components/modals/ConfirmDeleteModal";
import InlineError from "@components/forms/InlineError";
import usePromoCodeBatches from "@hooks/promoCodeBatches/usePromoCodeBatches";
import { listToursRequest } from "@services/tours/toursApi";
import { listStartPointsByTourRequest } from "@services/startPoints/startPointsApi";
import "@assets/css/promo_code_batches/panel.css";

const pageLimit = 20;
const detailLimit = 20;
const codeStatuses = ["all", "available", "redeemed", "inactive"];
const selectableContentStatuses = new Set([3, 5]);

const createBatchSchema = yup.object({
  partnerName: yup
    .string()
    .trim()
    .max(160, "Partner name cannot exceed 160 characters.")
    .required("Partner name is required."),
  tourId: yup.string().required("Tour is required."),
  startPointId: yup.string().required("Starting Point is required."),
  name: yup.string().trim().max(160).required("Batch name is required."),
  quantity: yup
    .number()
    .typeError("Quantity is required.")
    .integer("Quantity must be a whole number.")
    .min(1, "Quantity must be at least 1.")
    .max(5000, "Quantity cannot exceed 5000.")
    .required("Quantity is required."),
  codePrefix: yup
    .string()
    .trim()
    .max(20, "Prefix cannot exceed 20 characters."),
  codeLength: yup
    .number()
    .typeError("Code length is required.")
    .integer("Code length must be a whole number.")
    .min(6, "Code length must be at least 6.")
    .max(20, "Code length cannot exceed 20.")
    .required("Code length is required."),
});

function createInitialValues() {
  return {
    partnerName: "",
    tourId: "",
    startPointId: "",
    name: "",
    quantity: 1,
    codePrefix: "",
    codeLength: 8,
  };
}

function getLocalizedName(value) {
  if (typeof value === "string") return value || "N/A";
  if (!value || typeof value !== "object") return "N/A";
  const resolveEntry = (entry) => {
    if (typeof entry === "string") return entry;
    return entry?.value || entry?.message || entry?.text || "";
  };
  return (
    resolveEntry(value.en) ||
    resolveEntry(value.es) ||
    Object.values(value).map(resolveEntry).find(Boolean) ||
    "N/A"
  );
}

function getStartPointId(point = {}) {
  return String(point.uuid || point.stp_id || point.stpId || point.id || "");
}

function isSelectableBatchContent(item = {}) {
  return (
    item?.isActive !== false &&
    selectableContentStatuses.has(Number(item?.status))
  );
}

function getBatchStats(batch = {}) {
  return {
    total: Number(batch?.stats?.total ?? batch?.quantity ?? 0),
    available: Number(batch?.stats?.available ?? 0),
    redeemed: Number(batch?.stats?.redeemed ?? 0),
    inactive: Number(batch?.stats?.inactive ?? 0),
  };
}

function PromoCodeBatchesPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [tours, setTours] = useState([]);
  const [toursLoading, setToursLoading] = useState(false);
  const [startPoints, setStartPoints] = useState([]);
  const [startPointsLoading, setStartPointsLoading] = useState(false);
  const [selectorError, setSelectorError] = useState("");
  const [detailStatus, setDetailStatus] = useState("all");
  const [detailPage, setDetailPage] = useState(1);
  const [deleteBatchTarget, setDeleteBatchTarget] = useState(null);
  const lastDetailRequestRef = useRef("");
  const {
    batches,
    pagination,
    selectedBatch,
    loading,
    detailLoading,
    submitting,
    exporting,
    deleting,
    error,
    fetchBatches,
    fetchBatchDetail,
    createBatch,
    exportBatch,
    deleteBatch,
    clearSelectedBatch,
  } = usePromoCodeBatches();

  const formik = useFormik({
    initialValues: createInitialValues(),
    validationSchema: createBatchSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      const payload = {
        partnerName: values.partnerName.trim(),
        startPointId: values.startPointId,
        name: values.name.trim(),
        quantity: Number(values.quantity),
        codePrefix: values.codePrefix.trim().toUpperCase(),
        codeLength: Number(values.codeLength),
        promoCodeName: values.name.trim(),
        percentage: 100,
      };
      try {
        await createBatch(payload);
      } catch (requestError) {
        void requestError;
        return;
      }
      setIsCreateOpen(false);
      setDetailStatus("all");
      setDetailPage(1);
      formik.resetForm({ values: createInitialValues() });
      await fetchBatches({ page, limit: pageLimit, search });
    },
  });

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      fetchBatches({ page, limit: pageLimit, search });
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [fetchBatches, page, search]);

  useEffect(() => {
    if (!isCreateOpen || tours.length) return;
    let mounted = true;
    setToursLoading(true);
    listToursRequest({ page: 1, pageSize: 100 })
      .then((response) => {
        if (!mounted) return;
        const tourItems = response?.body?.tours || response?.body?.items || [];
        setTours(
          (Array.isArray(tourItems) ? tourItems : []).filter(
            isSelectableBatchContent,
          ),
        );
      })
      .catch((requestError) => {
        if (mounted) setSelectorError(requestError.message);
      })
      .finally(() => {
        if (mounted) setToursLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isCreateOpen, tours.length]);

  useEffect(() => {
    const tourId = formik.values.tourId;
    if (!isCreateOpen || !tourId) {
      setStartPoints([]);
      return;
    }
    let mounted = true;
    setStartPointsLoading(true);
    listStartPointsByTourRequest(tourId)
      .then((items) => {
        if (mounted) {
          setStartPoints(
            (Array.isArray(items) ? items : []).filter(
              isSelectableBatchContent,
            ),
          );
        }
      })
      .catch((requestError) => {
        if (mounted) setSelectorError(requestError.message);
      })
      .finally(() => {
        if (mounted) setStartPointsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [formik.values.tourId, isCreateOpen]);

  useEffect(() => {
    if (!selectedBatch?.id || isCreateOpen) return;
    const requestKey = `${selectedBatch.id}|${detailPage}|${detailStatus}`;
    if (lastDetailRequestRef.current === requestKey) return;
    lastDetailRequestRef.current = requestKey;
    fetchBatchDetail(selectedBatch.id, {
      page: detailPage,
      limit: detailLimit,
      status: detailStatus,
    });
  }, [
    detailPage,
    detailStatus,
    fetchBatchDetail,
    isCreateOpen,
    selectedBatch?.id,
  ]);

  const detailStats = useMemo(
    () => getBatchStats(selectedBatch),
    [selectedBatch],
  );
  const detailPagination = selectedBatch?.codesPagination || {
    page: 1,
    total: selectedBatch?.codes?.length || 0,
    totalPages: 1,
  };

  function getFieldError(fieldName) {
    return formik.touched[fieldName] ? formik.errors[fieldName] || "" : "";
  }

  function openCreateModal() {
    formik.resetForm({ values: createInitialValues() });
    setStartPoints([]);
    setSelectorError("");
    setIsCreateOpen(true);
  }

  function closeCreateModal() {
    if (submitting) return;
    setIsCreateOpen(false);
  }

  function openBatchDetail(batchId) {
    setDetailStatus("all");
    setDetailPage(1);
    lastDetailRequestRef.current = `${batchId}|1|all`;
    fetchBatchDetail(batchId, { page: 1, limit: detailLimit, status: "all" });
  }

  return (
    <CmsLayout activeModule="promo-code-batches" title="Promo Code Batches">
      <section className="promo-batches-page">
        <article className="panel table-panel promo-batches-panel">
          <div className="promo-batches-head">
            <div>
              <p className="promo-batches-kicker">Commercial promo codes</p>
              <h2>Promo Code Batches</h2>
            </div>
            <div className="promo-batches-head-actions">
              <label className="promo-batches-search">
                <IconSearch size={18} />
                <input
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder="Search batch or prefix"
                />
              </label>
              <button
                type="button"
                className="app-icon-btn app-icon-btn--add add-batches-btn"
                onClick={openCreateModal}
                title="Generate promo code batch"
                aria-label="Generate promo code batch"
              >
                <IconPlus size={17} />
              </button>
            </div>
          </div>

          {error ? (
            <p className="message-error promo-batches-message">{error}</p>
          ) : null}
          {loading ? (
            <p className="promo-batches-loading">Loading batches...</p>
          ) : null}

          <div className="table-wrap promo-batches-table-wrap">
            <table className="data-table promo-batches-table">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Actions</th>
                  <th>Partner</th>
                  <th>Tour / Starting Point</th>
                  <th>Quantity</th>
                  <th>Available</th>
                  <th>Redeemed</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => {
                  const stats = getBatchStats(batch);
                  return (
                    <tr key={batch.id}>
                      <td>
                        <div className="promo-batches-batch-cell">
                          <span className="promo-batches-avatar">
                            <IconTicket size={20} />
                          </span>
                          <div>
                            <strong>{batch.name || "N/A"}</strong>
                            <small>{batch.codePrefix || "No prefix"}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="promo-batches-row-actions">
                          <button
                            type="button"
                            className="app-icon-btn app-icon-btn--update"
                            onClick={() => openBatchDetail(batch.id)}
                            title="View batch detail"
                            aria-label="View batch detail"
                          >
                            <IconEye size={17} />
                          </button>
                          <button
                            type="button"
                            className="app-icon-btn app-icon-btn--update"
                            onClick={() => exportBatch(batch.id, "all")}
                            disabled={exporting}
                            title="Export all codes"
                            aria-label="Export all codes"
                          >
                            <IconDownload size={17} />
                          </button>
                        </div>
                      </td>
                      <td>
                        <strong>{batch.partnerName || "N/A"}</strong>
                      </td>
                      <td>
                        <strong>{batch.tour?.name || "N/A"}</strong>
                        <small>{batch.startPoint?.name || "=N/A"}</small>
                      </td>
                      <td>{stats.total}</td>
                      <td>
                        <span className="promo-batches-status is-available">
                          {stats.available}
                        </span>
                      </td>
                      <td>
                        <span className="promo-batches-status is-redeemed">
                          {stats.redeemed}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {!batches.length && !loading ? (
                  <tr>
                    <td colSpan={7}>No promo code batches found.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="promo-batches-pagination">
            <button
              type="button"
              className="button-ghost"
              disabled={pagination.page <= 1 || loading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <IconChevronLeft size={16} /> Previous
            </button>
            <span>
              Page {pagination.page} of {pagination.totalPages} ·{" "}
              {pagination.total} batches
            </span>
            <button
              type="button"
              className="button-ghost"
              disabled={pagination.page >= pagination.totalPages || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              Next <IconChevronRight size={16} />
            </button>
          </div>
        </article>
      </section>

      <AppModal
        isOpen={isCreateOpen}
        onClose={closeCreateModal}
        title="Generate Promo Code Batch"
        size="xl"
        className="promo-batches-modal"
      >
        <form className="promo-batches-form" onSubmit={formik.handleSubmit}>
          {selectorError ? (
            <p className="message-error">{selectorError}</p>
          ) : null}
          <div className="promo-batches-form-grid">
            <div className="tour-add-form-group promo-batches-field-wide">
              <p className="tour-add-form-label">
                partner name
                {getFieldError("partnerName") ? (
                  <InlineError>{getFieldError("partnerName")}</InlineError>
                ) : null}
              </p>
              <label className="field-label app-form-field">
                <input
                  className="field-input"
                  name="partnerName"
                  value={formik.values.partnerName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  maxLength={160}
                  placeholder="Example: Booking.com"
                />
              </label>
            </div>

            <div className="tour-add-form-group">
              <p className="tour-add-form-label">
                tour
                {getFieldError("tourId") ? (
                  <InlineError>{getFieldError("tourId")}</InlineError>
                ) : null}
              </p>
              <label className="field-label app-form-field">
                <select
                  className="field-input select-control"
                  name="tourId"
                  value={formik.values.tourId}
                  onChange={(event) => {
                    formik.setFieldValue("tourId", event.target.value, true);
                    formik.setFieldValue("startPointId", "", false);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={toursLoading}
                >
                  <option value="">
                    {toursLoading ? "Loading tours..." : "Select tour"}
                  </option>
                  {tours.map((tour) => (
                    <option key={tour.id} value={tour.id}>
                      {getLocalizedName(tour.name)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="tour-add-form-group">
              <p className="tour-add-form-label">
                starting point
                {getFieldError("startPointId") ? (
                  <InlineError>{getFieldError("startPointId")}</InlineError>
                ) : null}
              </p>
              <label className="field-label app-form-field">
                <select
                  className="field-input select-control"
                  name="startPointId"
                  value={formik.values.startPointId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={!formik.values.tourId || startPointsLoading}
                >
                  <option value="">
                    {startPointsLoading
                      ? "Loading Starting Points..."
                      : "Select Starting Point"}
                  </option>
                  {startPoints.map((point) => (
                    <option
                      key={getStartPointId(point)}
                      value={getStartPointId(point)}
                    >
                      {getLocalizedName(point.name)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {[
              ["name", "batch name", "text"],
              ["quantity", "quantity", "number"],
              ["codePrefix", "code prefix", "text"],
              ["codeLength", "random code length", "number"],
            ].map(([fieldName, label, type]) => (
              <div key={fieldName} className="tour-add-form-group">
                <p className="tour-add-form-label">
                  {label}
                  {getFieldError(fieldName) ? (
                    <InlineError>{getFieldError(fieldName)}</InlineError>
                  ) : null}
                </p>
                <label className="field-label app-form-field">
                  <input
                    className="field-input"
                    type={type}
                    name={fieldName}
                    value={formik.values[fieldName]}
                    min={
                      fieldName === "quantity"
                        ? 1
                        : fieldName === "codeLength"
                          ? 6
                          : undefined
                    }
                    max={
                      fieldName === "quantity"
                        ? 5000
                        : fieldName === "codeLength"
                          ? 20
                          : undefined
                    }
                    step={type === "number" ? 1 : undefined}
                    onChange={(event) => {
                      const value =
                        fieldName === "codePrefix"
                          ? String(event.target.value || "")
                              .toUpperCase()
                              .replace(/[^A-Z0-9]/g, "")
                          : event.target.value;
                      formik.setFieldValue(fieldName, value, true);
                    }}
                    onBlur={formik.handleBlur}
                  />
                </label>
              </div>
            ))}
          </div>

          <div className="promo-batches-save-row">
            <button
              type="submit"
              className={`app-icon-btn app-icon-btn--add ${submitting ? "is-loading" : ""}`}
              disabled={!formik.isValid || submitting}
              title="Generate batch"
              aria-label="Generate batch"
            >
              {submitting ? (
                <span className="mode-route-tool-btn-spinner" />
              ) : (
                <IconDeviceFloppy size={17} />
              )}
            </button>
          </div>
        </form>
      </AppModal>

      <AppModal
        isOpen={Boolean(selectedBatch) || detailLoading}
        onClose={clearSelectedBatch}
        title={selectedBatch?.name || "Promo Code Batch Detail"}
        size="xl"
        className="promo-batches-modal"
      >
        {detailLoading && !selectedBatch ? (
          <p>Loading batch detail...</p>
        ) : null}
        {selectedBatch ? (
          <div className="promo-batches-detail">
            <div className="promo-batches-detail-grid">
              <div>
                <span>Partner</span>
                <strong>{selectedBatch.partnerName || "N/A"}</strong>
              </div>
              <div>
                <span>Tour</span>
                <strong>{selectedBatch.tour?.name || "N/A"}</strong>
              </div>
              <div>
                <span>Starting Point</span>
                <strong>{selectedBatch.startPoint?.name || "N/A"}</strong>
              </div>
              <div>
                <span>Percentage</span>
                <strong>{selectedBatch.percentage ?? 100}%</strong>
              </div>
            </div>
            <div className="promo-batches-stat-grid">
              <div>
                <span>Total</span>
                <strong>{detailStats.total}</strong>
              </div>
              <div>
                <span>Available</span>
                <strong>{detailStats.available}</strong>
              </div>
              <div>
                <span>Redeemed</span>
                <strong>{detailStats.redeemed}</strong>
              </div>
              <div>
                <span>Inactive</span>
                <strong>{detailStats.inactive}</strong>
              </div>
            </div>
            <div className="promo-batches-detail-toolbar">
              <label className="field-label app-form-field">
                <select
                  className="field-input select-control"
                  value={detailStatus}
                  onChange={(event) => {
                    setDetailStatus(event.target.value);
                    setDetailPage(1);
                  }}
                >
                  {codeStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="button-primary promo-batches-export-btn"
                onClick={() => exportBatch(selectedBatch.id, detailStatus)}
                disabled={exporting}
              >
                <IconDownload size={17} />{" "}
                {exporting ? "Exporting..." : "Export CSV"}
              </button>
            </div>
            {detailLoading ? <p>Loading codes...</p> : null}
            <div className="table-wrap promo-batches-codes-wrap">
              <table className="data-table promo-batches-codes-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Discount</th>
                    <th>Status</th>
                    <th>Uses</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedBatch.codes || []).map((code) => (
                    <tr key={code.id || code.code}>
                      <td>
                        <strong>{code.code}</strong>
                      </td>
                      <td>{code.name || "N/A"}</td>
                      <td>{code.percentage ?? 0}%</td>
                      <td>
                        <span
                          className={`promo-batches-status is-${code.status || "inactive"}`}
                        >
                          {code.status || "inactive"}
                        </span>
                      </td>
                      <td>
                        {code.usedCount ?? 0}/{code.maxUses ?? 1}
                      </td>
                    </tr>
                  ))}
                  {!selectedBatch.codes?.length && !detailLoading ? (
                    <tr>
                      <td colSpan={5}>No codes found for this status.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="promo-batches-pagination">
              <button
                type="button"
                className="button-ghost"
                disabled={detailPage <= 1 || detailLoading}
                onClick={() =>
                  setDetailPage((current) => Math.max(1, current - 1))
                }
              >
                <IconChevronLeft size={16} /> Previous
              </button>
              <span>
                Page {detailPagination.page} of {detailPagination.totalPages} ·{" "}
                {detailPagination.total} codes
              </span>
              <button
                type="button"
                className="button-ghost"
                disabled={
                  detailPage >= detailPagination.totalPages || detailLoading
                }
                onClick={() => setDetailPage((current) => current + 1)}
              >
                Next <IconChevronRight size={16} />
              </button>
            </div>
            <div className="promo-batches-delete-zone">
              <button
                type="button"
                className="app-icon-btn app-icon-btn--delete promo-batches-delete-btn"
                onClick={() => setDeleteBatchTarget(selectedBatch)}
                disabled={deleting}
                title="Delete promo code batch"
                aria-label="Delete promo code batch"
              >
                {deleting ? (
                  <span className="mode-route-tool-btn-spinner" />
                ) : (
                  <IconTrash size={15} />
                )}
              </button>
            </div>
          </div>
        ) : null}
      </AppModal>

      <ConfirmDeleteModal
        isOpen={Boolean(deleteBatchTarget)}
        onClose={() => {
          if (!deleting) setDeleteBatchTarget(null);
        }}
        submitting={deleting}
        title="Delete promo code batch"
        description="Are you sure you want to delete this promo code batch and all its generated codes?"
        preview={
          <p>
            <strong>{deleteBatchTarget?.name || "N/A"}</strong>
            {deleteBatchTarget?.partnerName
              ? ` - ${deleteBatchTarget.partnerName}`
              : ""}
          </p>
        }
        onConfirm={async () => {
          const batchId = deleteBatchTarget?.id;
          if (!batchId) return;
          try {
            await deleteBatch(batchId);
            setDeleteBatchTarget(null);
            await fetchBatches({ page, limit: pageLimit, search });
          } catch (requestError) {
            void requestError;
          }
        }}
      />
    </CmsLayout>
  );
}

export default PromoCodeBatchesPage;
